import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import MedicalMalpracticeModal from './MedicalMalpracticeModal';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import {
  saveProviderMalpracticeInfo,
  deleteProviderMalpracticeInfo,
  getAllProviderMalpracticeInfo,
} from '../../Services/ProviderService';

const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

export default function MedicalMalpractice({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [items, setItems] = useState(Array.isArray(initialData?.malpracticeInsurance) ? initialData.malpracticeInsurance : []);
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });

  const getProviderIdFromStorage = () => {
    try {
      return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read provider id from localStorage:', error);
      return null;
    }
  };
  const getOrganizationIdFromStorage = () => {
    try {
      return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read organization id from localStorage:', error);
      return null;
    }
  };

  const refreshMalpracticeInsurance = async () => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    if (!token || !providerIdToUse) return;

    setLoading(true);
    try {
      const response = await getAllProviderMalpracticeInfo(token, providerIdToUse);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setItems(refreshed);
        if (onSaveSuccess) onSaveSuccess(refreshed);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        //toast.error(response?.message || 'Failed to load malpractice insurance records.');
        console.error('Failed to load malpractice insurance records:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error loading malpractice insurance:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems(Array.isArray(initialData?.malpracticeInsurance) ? initialData.malpracticeInsurance : []);
  }, [initialData]);

  useEffect(() => {
    if (!token || !providerId) return;
    if (Array.isArray(initialData?.malpracticeInsurance) && initialData.malpracticeInsurance.length > 0) return;
    refreshMalpracticeInsurance();
  }, [token, providerId, initialData]);

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingIndex(null);
  };

  const buildPayload = (values) => {
    const providerIdToUse = providerId || getProviderIdFromStorage();
    const organizationIdToUse = values.organization_id || getOrganizationIdFromStorage() || null;
    const isUpdate = Boolean(values.id || values.Id || values.malpractice_insurance_id);

    return {
      ...values,
      provider_id: providerIdToUse,
      organization_id: organizationIdToUse,
      type: isUpdate ? 'update' : 'create',
    };
  };

  const handleSave = async (values) => {
    const payload = buildPayload(values);
    if (!payload.provider_id) {
      toast.error('Provider ID is required to save malpractice insurance.');
      return;
    }

    setSaving(true);
    try {
      const response = await saveProviderMalpracticeInfo(token, payload);
      if (response?.is_success) {
        await refreshMalpracticeInsurance();
        setShowModal(false);
        setEditingIndex(null);
        toast.success('Malpractice insurance saved successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save malpractice insurance.');
      }
    } catch (error) {
      console.error('Error saving malpractice insurance:', error);
      toast.error(error?.message || 'Failed to save malpractice insurance.');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const originalItems = [...items];

    if (!item || !(item.id || item.Id || item.malpractice_insurance_id)) {
      return;
    }

    setDeletingIndex(index);
    try {
      const id = item.id || item.Id || item.malpractice_insurance_id;
      const response = await deleteProviderMalpracticeInfo(token, id);
      if (response?.is_success) {
        const refreshed = await refreshMalpracticeInsurance();
        if (Array.isArray(refreshed)) {
          setItems(refreshed);
          if (onSaveSuccess) onSaveSuccess(refreshed);
        } else {
          const updated = items.filter((_, idx) => idx !== index);
          setItems(updated);
          if (onSaveSuccess) onSaveSuccess(updated);
        }
        toast.success('Malpractice insurance deleted successfully.');
      } else if (response?.is_success === false && response?.status_code === 500) {
        setItems(originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        setItems(originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        toast.error(response?.message || 'Failed to delete malpractice insurance.');
      }
    } catch (error) {
      console.error('Error deleting malpractice insurance:', error);
      setItems(originalItems);
      if (onSaveSuccess) onSaveSuccess(originalItems);
      toast.error(error?.message || 'Failed to delete malpractice insurance.');
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Medical Malpractice Insurance</h6>
          <button
            type="button"
            className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2"
            onClick={handleAdd}
          >
            <MdAdd size={18} /> Add
          </button>
        </div>

      
        {loading ?(
          <div className="text-center py-4 text-muted small">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-4 text-muted small">
             No malpractice insurance records added yet. Click "Add" to create one.
          </div>
        ) : (

        <div className="row g-3 mb-3">
          {
            items.map((item, index) => (
              <React.Fragment key={index}>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Insurer
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.insurer_name || 'Unnamed insurer'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Policy
                  </div>

                  <div className="fw-semibold text-dark">
                    {item.policy || 'N/A'}
                  </div>

                </div>
              </div>
            </div>



            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Effective Date
                  </div>

                  <div className="fw-semibold text-dark">
                  {item.policy_effective_date || 'N/A'}
                  </div>

                </div>
              </div>
            </div>


            <div className="col-12 col-md-6 col-lg-3">
              <div className="card h-100 border-0 shadow-lg rounded-4">
                <div className="card-body">

                  <div className="text-muted small mb-2">
                    Actions
                  </div>

                  <div className="d-flex gap-2">

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary rounded-3"
                      onClick={() => handleEdit(index)}
                    >
                      <MdEdit size={16} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger rounded-3"
                      onClick={() => openDeleteModal(index)}
                    >
                      <MdDelete size={16} />
                    </button>

                  </div>

                </div>
              </div>
            </div> 
              </React.Fragment>

            ))}
          </div>

          )}

        <MedicalMalpracticeModal
          isOpen={showModal}
          providerId={providerId}
          onClose={handleClose}
          onSave={handleSave}
          initialData={editingIndex !== null ? items[editingIndex] : null}
          isLoading={saving}
        />
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="malpractice insurance record"
        itemName={deleteModal.item ? (deleteModal.item.insurer_name || deleteModal.item.policy || 'this malpractice insurance record') : 'this malpractice insurance record'}
        description="This will permanently delete this malpractice insurance record. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}
